import { useEffect, useRef, useState } from "react";
import C from "classnames";
import { BreadCrumb } from "primereact/breadcrumb";
import { Skeleton } from "primereact/skeleton";
import { LessonsContainer, PathDetails } from "sj-ui-toolkit/src/atlas/components";
import { ContentCardSkeleton } from "sj-ui-toolkit/src/atlas/components/ContentCard";
import type { ContentCardProps } from "sj-ui-toolkit/src/atlas/components/ContentCard";
import { getContentClient } from "sj-ui-toolkit/src/client/svcContent";
import { getPathByCollectionKey } from "sj-ui-toolkit/src/api/user";
import { transformPathData } from "sj-ui-toolkit/src/atlas/utils";
import { INTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";

import "sj-ui-toolkit/src/atlas/styles/pathBackgrounds.scss";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PathPreviewProps {
  variant?: string;
  map?: number;
}

type PathData = ReturnType<typeof transformPathData>;
type TransformedLesson = PathData["remainingLessons"][number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const mapLesson = (lesson: TransformedLesson): ContentCardProps => ({
  id: String(lesson.id),
  contentType: lesson.contentType,
  lessonType: lesson.contentType,
  description: lesson.description,
  title: lesson.title,
  estimatedTime: String(lesson.estimatedTime),
  difficulty: lesson.difficulty,
  status: lesson.status,
  lessonKey: lesson.lessonKey,
  ...(lesson.points !== undefined && { points: lesson.points }),
  required: lesson.required,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SKELETON_LESSON_COUNT = 5;

export const PathPreview = ({ variant, map }: PathPreviewProps) => {
  const [pathData, setPathData] = useState<PathData | null>(null);
  const [loading, setLoading] = useState(true);
  const [breadcrumbs, setBreadcrumbs] = useState<{ label: string }[]>([]);
  const hasLoadedRef = useRef(false);

  const bgClass = C(`path-bg-${map}-${variant}`, "min-h-full");

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const fetchPathData = async () => {
      try {
        const collections = await getContentClient().getCollections();
        const collection = collections.find(c => c.collectionKey && (c.lessonCount ?? 0) > 4);
        if (!collection?.collectionKey) {
          setLoading(false);
          return;
        }

        const response = await getPathByCollectionKey(collection.collectionKey);
        if (!response) {
          setLoading(false);
          return;
        }

        const formattedPath = transformPathData(response);
        setPathData(formattedPath);
        setBreadcrumbs([{ label: formattedPath.pathDetails.title }]);
      } catch (error) {
        console.error("Error fetching path data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPathData();
  }, []);

  if (loading) {
    return (
      <div className={bgClass}>
        {/* Breadcrumb skeleton */}
        <div className="px-4 py-2">
          <Skeleton width="200px" height="1rem" />
        </div>

        {/* PathDetails skeleton */}
        <div className="pb-8">
          <div className="flex items-center justify-between gap-8 mb-8">
            <Skeleton width="60px" height="24px" />
            <Skeleton width="130px" height="40px" />
          </div>
          <div className="flex flex-col gap-4 mb-8">
            <Skeleton width="60%" height="2rem" />
            <Skeleton width="80%" height="1.25rem" />
            <Skeleton width="65%" height="1.25rem" />
          </div>
          <div className="flex gap-8">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton width="100px" height="0.875rem" />
                <Skeleton width="3rem" height="2.5rem" />
              </div>
            ))}
          </div>
        </div>

        {/* LessonsContainer skeleton */}
        <div className="py-8">
          <Skeleton width="200px" height="1.25rem" className="mb-4" />
          <div className="flex flex-wrap gap-6 mt-4">
            {Array.from({ length: SKELETON_LESSON_COUNT }).map((_, i) => (
              <div key={i} style={{ width: "300px", minWidth: "300px" }}>
                <ContentCardSkeleton />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={bgClass}>
      <BreadCrumb model={breadcrumbs} home={{ label: INTERNAL_ROUTES.Learn.name }} />

      {pathData !== null && (
        <PathDetails
          title={pathData.pathDetails.title}
          description={pathData.pathDetails.description}
          enrolledInPath={pathData.pathDetails.enrolledInPath}
          pointsEarned={pathData.pathDetails.pointsEarned ?? 0}
          totalPoints={pathData.pathDetails.totalPoints ?? 0}
          lessonsCompleted={pathData.pathDetails.lessonsCompleted}
          lessonsInPath={pathData.pathDetails.lessonsInPath}
          estimatedTimeRemaining={Number(pathData.pathDetails.estimatedTimeRemaining)}
        />
      )}

      {pathData !== null && pathData.remainingLessons.length > 0 && (
        <LessonsContainer
          lessons={pathData.remainingLessons.map(mapLesson)}
          title="Lessons to do"
        />
      )}

      {pathData !== null && pathData.completedLessons.length > 0 && (
        <LessonsContainer
          lessons={pathData.completedLessons.map(mapLesson)}
          title="Completed Lessons"
        />
      )}
    </div>
  );
};
